/**
 * Customer Portal (UI) - shared draft shapes and helper builders for Webhooks settings.
 *
 * This module centralizes create/edit draft initialization so the Settings route and
 * modal forms stay in sync when fields are added or adjusted.
 */
import type {
  Brand,
  CallbackProfile,
  CallbackProfileUpsertInput,
  Campaign,
  PhoneNumber,
  ScopeType,
  WebhookEventAssignment,
  WebhookTypeId
} from '../../types/portal';

export type CallbackProfileDraft = {
  profileId?: string;
  name: string;
  description: string;
  url: string;
  method: 'POST' | 'PUT';
  enabled: boolean;
  contentType: string;
  authType: CallbackProfileUpsertInput['authType'];
  authBearerToken: string;
  authBasicUsername: string;
  authBasicPassword: string;
  authApiKeyHeader: string;
  authApiKeyValue: string;
  authHmacSecret: string;
  authHmacHeader: string;
  authHmacTimestampHeader: string;
  authHmacIdHeader: string;
  authHmacPrefix: string;
  authHmacIncludeTimestamp: boolean;
};

export type EventAssignmentDraft = {
  assignmentId?: string;
  webhookType: WebhookTypeId;
  scopeType: ScopeType;
  scopeId: string;
  profileId: string;
  enabled: boolean;
  priority: number;
};

export type ScopeOptionData = {
  accountId: string | null;
  brands: Brand[];
  campaigns: Campaign[];
  phoneNumbers: PhoneNumber[];
};

/**
 * Build a profile draft from an existing profile row or as a blank create draft.
 */
export function createProfileDraft(profile?: CallbackProfile): CallbackProfileDraft {
  return {
    profileId: profile?.profileId,
    name: profile?.name ?? '',
    description: profile?.description ?? '',
    url: profile?.url ?? '',
    method: profile?.method ?? 'POST',
    enabled: profile?.enabled ?? true,
    contentType: profile?.contentType ?? 'application/json',
    authType: profile?.authType ?? 'NONE',
    authBearerToken: '',
    authBasicUsername: profile?.authBasicUsername ?? '',
    authBasicPassword: '',
    authApiKeyHeader: profile?.authApiKeyHeader ?? '',
    authApiKeyValue: '',
    authHmacSecret: '',
    authHmacHeader: profile?.authHmacHeader ?? 'X-Pronto-Signature',
    authHmacTimestampHeader: profile?.authHmacTimestampHeader ?? 'X-Pronto-Timestamp',
    authHmacIdHeader: profile?.authHmacIdHeader ?? '',
    authHmacPrefix: profile?.authHmacPrefix ?? '',
    authHmacIncludeTimestamp: profile?.authHmacIncludeTimestamp ?? true
  };
}

/**
 * Build an event assignment draft from an existing assignment row or defaults.
 */
export function createAssignmentDraft(
  accountId: string,
  assignment?: WebhookEventAssignment,
  fallbackWebhookType: WebhookTypeId = 'delivery_report'
): EventAssignmentDraft {
  return {
    assignmentId: assignment?.assignmentId,
    webhookType: assignment?.webhookType ?? fallbackWebhookType,
    scopeType: assignment?.scopeType ?? 'ACCOUNT',
    scopeId: assignment?.scopeId ?? accountId,
    profileId: assignment?.profileId ?? '',
    enabled: assignment?.enabled ?? true,
    priority: assignment?.priority ?? 0
  };
}

/**
 * Human-readable callback profile label used in assignment selectors.
 */
export function profileDisplayName(profile: CallbackProfile): string {
  return `${profile.name} (${profile.method})`;
}

/**
 * Return scope options for the selected scope type from already-loaded scope catalogs.
 */
export function assignmentScopeOptions(
  scopeType: ScopeType,
  { accountId, brands, campaigns, phoneNumbers }: ScopeOptionData
): Array<{ id: string; label: string }> {
  if (scopeType === 'ACCOUNT') {
    return accountId ? [{ id: accountId, label: accountId }] : [];
  }
  if (scopeType === 'BRAND') {
    return brands.map((brand) => ({ id: brand.id, label: brand.name }));
  }
  if (scopeType === 'CAMPAIGN') {
    return campaigns.map((campaign) => ({ id: campaign.id, label: campaign.name }));
  }
  return phoneNumbers.map((phoneNumber) => ({ id: phoneNumber.id, label: `${phoneNumber.label} (${phoneNumber.e164})` }));
}
